import {
  WSAlert,
  WSBreadcrumbsItem,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSPill,
  WSPoster,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  Payer,
  PayeeRequirement,
  PayerStatus,
  PayerTaxInformationShareStatus
} from "@wingspanhq/wingspan-api-sdk";
import {
  queriesPayerEngagements,
  queriesPayers,
  queriesRequirements
} from "@query";
import { useQueryPayerQuickbooksMapping } from "@hooks/useQueryPayerQuickbooksMapping";
import sortBy from "lodash/sortBy";
import React, { useCallback, useMemo, useState } from "react";
import { Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import { Tabs } from "../../../../components/Tabs";
import { WSQueries } from "../../../../query/WSQuery";
import { useModalShareTaxInformationWithPayers } from "../../../../shared/components/ModalShareTaxInformationWithPayers/useModalShareTaxInformationWithPayers";
import { ResourceHistory } from "../../../../shared/components/ResourceHistory";
import { selectorExpiredRequirements } from "../../../Requirements/selectors/selectorExpiredRequirements";
import { selectorExpiringRequirements } from "../../../Requirements/selectors/selectorExpiringRequirements";
import { selectorIsRequirementArchived } from "../../../Requirements/selectors/selectorIsRequirementArchived";
import { AutoPay } from "../../components/AutoPay";
import { ClientInfoPanel } from "../../components/ClientInfoPanel";
import { useEditClientModal } from "../../components/EditClientModal/useEditClientModal";
import { EngagementsTable } from "../../components/EngagementsTable";
import { PayerIntegrationWidget } from "../../components/IntegrationsSection/PayerIntegrationWidget";
import { usePayerHistoryResources } from "../../hooks/usePayerHistoryResources";
import {
  getPayerDetailsRequirementsUrl,
  getPayerDetailsUrl,
  getPayerHistoryUrl,
  getPayerRequirementDetailUrl,
  PAYER_DETAILS_PATH,
  PAYER_DETAILS_REQUIREMENTS_PATH,
  PAYERS_PATH
} from "../../paths";
import { selectorAreAllPayerEngagementRequirementsComplete } from "../../selectors/selectorAreAllPayerEngagementRequirementsComplete";
import { selectorHasEligiblePayerEngagement } from "../../selectors/selectorHasEligiblePayerEngagement";
import { selectorIsPayerEngagementActive } from "../../selectors/selectorIsPayerEngagementActive";
import { selectorNamedPayerEngagements } from "../../selectors/selectorNamedPayerEngagements";
import { selectorPayerDisplayName } from "../../selectors/selectorPayerDisplayName";
import { useArchiveClientModal } from "./ArchiveClientModal";
import { useRestoreClientModal } from "./RestoreClientModal";
import { RecentInvoicesWidget } from "./RecentInvoicesWidget";
import { WireTransferSection } from "./WireTransferSection";
import { PayerPayeeDetailsRequirements } from "../../../Requirements/components/PayerPayeeDetailsRequirements";
import { RequirementContextType } from "../../../Requirements/types";

// ============================================================================
// v3 migration — accepted breakage (directive: "not a single v1 endpoint may be
// called on this screen"; user-confirmed 2026-07-22).
//
// Everything the Client Details screen needs to READ (`getPayer`) and WRITE
// (edit / archive / share-tax-info) is on v3, and every read here is a DIRECT
// endpoint — no `/v3/search/*-rows` projection. Assignments reads
// `listPayerEngagements`, requirements read `listPayeeRequirements`, and the
// requirements backfill the per-engagement requirements/eligibility the lean
// CRUD `PayerEngagement` omits (see the selectorPayerEngagement* selectors). Wire
// transfers reads the audited v3 processing-account endpoint. The QuickBooks
// mapping panel is back on v3 (connection + reference data + the mapping write);
// two panels stay out, each for a reason verified against the deployed
// integration API, not inferred from the spec:
//   - Documents        → the payee cannot see the client's files at all. A file
//                        created with `payeeId` set DOES come back with the
//                        payee's account in `viewers`, but that grant is not
//                        honoured on read: as the payee, `getVaultFile` answers
//                        404 ResourceNotFound and the file is absent from
//                        `listVaultFiles`. Since this panel only ever READ files
//                        the other account uploaded, it can render nothing until
//                        the cross-account read works. (Separately,
//                        `listVaultFiles` filters on payeeId / visibility / kind
//                        / externalId only — the server 422s `metadata`, `tags`
//                        and `payerId` — so v1's `tags: ["payer:<id>"]` +
//                        `hidden` selection has no v3 equivalent either.)
//   - Autopay setup    → the off-state's only content was a share link to
//                        /autopay/:tag, and that whole flow (→ /payment/autopay/
//                        :payeeId → AutopaySetup) was removed from v3, so the
//                        link would point at a 404. `listTags` also still answers
//                        501. The panel shows the on-state only (status +
//                        default instrument, both from the client's own v3
//                        settings).
//
// Both already returned empty on a v3 payer id, so leaving them out is not a
// functional regression — it just removes dead v1 traffic. Preserved as comments
// for re-enablement. See V3_MIGRATION_TRACKER.md · /v3/payments/payers.
// ============================================================================

export const RouteClientDetails: React.FC = () => {
  const history = useHistory();

  const match = useRouteMatch<{ payerId: string }>();
  const payerId = match.params.payerId;
  const modalFormW9PayerConsent = useModalShareTaxInformationWithPayers();

  // v3 read: getPayer. The screen consumes the SDK `Payer` directly — no
  // adapter to the v1 shape.
  const queryClient = queriesPayers.getPayer.useQuery({ payerId });

  // The client ACCOUNT id the requirement list filters on.
  const payerAccountId = queryClient.data?.payerAccountId;

  // The client's payer-wide QuickBooks mapping, off the raw payer body — the
  // typed `Payer` drops `quickbooksItemId`. Not read from `PayerRow` (which
  // declares both): that row is a materialized search projection, so it lags the
  // mapping write and the panel would keep showing the previous item.
  const queryQuickbooksMapping = useQueryPayerQuickbooksMapping(payerId);

  // v3 read: this client's requirement INSTANCES. v1 filtered on `payerId` +
  // `payeeId`; on v3 the list is scoped by the client's account id, and the payee
  // side is implicit in the caller's own account.
  const queryRequirements =
    queriesRequirements.listPayeeRequirements.useQueryAll(
      { filter: { payerAccountId: { in: [payerAccountId as string] } } },
      { enabled: !!payerAccountId }
    );

  // Gated on the same condition the query is enabled by: a disabled React Query
  // sits at `status: "loading"` indefinitely, and `payerAccountId` is omitted for
  // an unlinked payer — so an unguarded flag would skeleton such a client's
  // requirements tab forever instead of showing its genuinely empty list.
  const isRequirementsLoading = !!payerAccountId && queryRequirements.isLoading;

  // Two v1 list guarantees have no v3 equivalent (tracker:
  // /v3/onboarding/payee-requirements — "requirement lists lost server-side sort
  // and the engagement-attached narrowing"). Both are recoverable client-side on
  // this screen, so it does NOT inherit the accepted "server order, unattached
  // requirements mixed in" breakage:
  //  - narrowing: v1 ran two reads split on `payerPayeeEngagementIds` existence;
  //    v3 has no existence operator, so one read is partitioned on
  //    `selectorIsRequirementArchived` — the codebase's canonical "not attached
  //    to any engagement" rule, same as the status pill and row actions use.
  //  - sort: v1 asked the server for `createdAt asc`; v3 has no sort param, but
  //    the instance carries `events.createdAt`. The tables re-sort by priority on
  //    top of this, and JS sort is stable, so creation order survives as the
  //    tiebreak within a priority band — as it did on v1.
  const allRequirements = sortBy(
    queryRequirements.flatData ?? [],
    requirement => requirement.events?.createdAt
  );
  const requirements = allRequirements.filter(
    requirement => !selectorIsRequirementArchived(requirement)
  );
  const archivedRequirements = allRequirements.filter(
    selectorIsRequirementArchived
  );

  // v3 read: assignments, straight off the CRUD collection endpoint rather than
  // the `searchPayerRows` projection — client search is the flakiest read on this
  // surface, and the collection read serves normally (only the SINGLE engagement
  // read is 503). The lean `PayerEngagement` carries no requirements and no
  // eligibility flag, so both are derived from `allRequirements` by the
  // selectorPayerEngagement* selectors at the point of use.
  const queryPayerEngagements =
    queriesPayerEngagements.listPayerEngagements.useQueryAll({ payerId });
  // Memoized so the create-invoice callback below keeps a stable identity —
  // `flatData` is a fresh array each render.
  const payerEngagements = useMemo(
    () => queryPayerEngagements.flatData ?? [],
    [queryPayerEngagements.flatData]
  );

  // --- v3-migration accepted breakage: v1 reads removed (see banner above) ---
  // Their imports are gone too, so re-enabling any of these means restoring the
  // import alongside the call.
  // const userId = useUserId();
  // const qDocuments = useQueryMemberPrivateFiles({
  //   filter: { hidden: true, tags: { in: [`payer:${payerId}`] } }
  // });
  // const queryFeatureFlags = useQueryFeatureFlags();
  // const queryQBO = useQueryIntegrationsQuickbooks();
  const historyResources = usePayerHistoryResources(payerId);

  const [showArchived, setShowArchived] = useState<boolean>(false);

  const archiveClientModal = useArchiveClientModal();
  const restoreClientModal = useRestoreClientModal();
  const editClientModal = useEditClientModal();

  const handleCreateInvoice = useCallback(() => {
    // Single-assignment auto-select, as on v1: a client with exactly one
    // assignment prefills that assignment, anything else prefills the email and
    // lets the invoice form pick. This was dropped as accepted breakage on the
    // grounds that the v1 payer-engagements read 503s — but only the SINGLE
    // engagement read does; the collection read above serves normally, so the
    // shortcut has a v3 source after all.
    const state =
      payerEngagements.length === 1
        ? {
            payerId,
            payerPayeeEngagementId: payerEngagements[0].id
          }
        : { email: queryClient.data?.email };

    history.push("/member/invoices/create", state);
  }, [history, payerId, payerEngagements, queryClient.data]);

  const handleEditClient = () => {
    editClientModal.open({
      client: queryClient.data as Payer
    });
  };

  // v3: restore reactivates the deactivated payer via `restorePayer` (WIN-15189).
  const handleRestoreClient = () => {
    restoreClientModal.open({
      client: queryClient.data as Payer
    });
  };

  return (
    <WSPage>
      <WSQueries
        queries={{
          queryClient
        }}
      >
        {({ queryClientData: client }) => {
          const clientName = selectorPayerDisplayName(client);

          const isInactive = client.status === PayerStatus.DEACTIVATED;
          const isNotShared =
            client.taxInformationShare?.status !==
            PayerTaxInformationShareStatus.ACTIVE;

          const filteredEngagements =
            selectorNamedPayerEngagements(payerEngagements);
          const activeFilteredEngagements = filteredEngagements.filter(
            selectorIsPayerEngagementActive
          );
          const inactiveFilteredEngagements = filteredEngagements.filter(
            engagement => !selectorIsPayerEngagementActive(engagement)
          );

          const activeEngagements = payerEngagements.filter(
            selectorIsPayerEngagementActive
          );
          const areRequirementsCompleted =
            selectorAreAllPayerEngagementRequirementsComplete({
              engagements: activeEngagements,
              requirements: allRequirements
            });
          const hasEligibleEngagements = selectorHasEligiblePayerEngagement({
            engagements: activeEngagements,
            requirements: allRequirements
          });

          const expiringRequirements =
            selectorExpiringRequirements(requirements);
          const expiredRequirements = selectorExpiredRequirements(requirements);

          return (
            <>
              <WSPageToolbar
                breadcrumbs={[
                  {
                    label: "Clients",
                    onClick: () => {
                      history.push(PAYERS_PATH);
                    }
                  },
                  ...(isInactive
                    ? [
                        {
                          label: "Archived clients",
                          onClick: () => {
                            history.push(`${PAYERS_PATH}/archived`);
                          }
                        } as WSBreadcrumbsItem
                      ]
                    : []),
                  {
                    label: clientName
                  }
                ]}
                // "Edit client" (updatePayer), "Archive" (deletePayer
                // soft-deactivate), and "Restore" (restorePayer, WIN-15189) are on
                // v3. Still gapped: "Create invoice" targets the v1 invoice-create
                // flow.
                actions={
                  isInactive
                    ? [
                        {
                          label: "Restore client",
                          icon: "repeat-circle",
                          onClick: () => {
                            handleRestoreClient();
                          }
                        }
                      ]
                    : [
                        {
                          icon: "plus",
                          label: "Create invoice",
                          buttonKind: "Primary",
                          onClick: handleCreateInvoice
                        },
                        {
                          icon: "edit",
                          label: "Edit client",
                          onClick: handleEditClient
                        },
                        {
                          icon: "archive",
                          label: "Archive client",
                          onClick: () => {
                            archiveClientModal.open({
                              client
                            });
                          }
                        }
                      ]
                }
              />
              <WSFlexBox.CenterY mb="XL">
                <WSText.Heading3 mr="L">{clientName}</WSText.Heading3>
                {client.status === PayerStatus.DEACTIVATED ? (
                  <WSPill theme="neutral" text="Archived" />
                ) : null}
              </WSFlexBox.CenterY>
              <WSList gap="L">
                {
                  // Registered but tax info not shared (Payer.taxInformationShare);
                  // the branches below derive from the v3 engagements +
                  // requirements reads. Order is significant — the first matching
                  // branch is the only alert shown.
                  isNotShared ? (
                    <WSAlert
                      icon="alert"
                      size="M"
                      theme="warning"
                      title="Tax information not shared"
                      action={{
                        onClick: () => {
                          modalFormW9PayerConsent.open({
                            payerId: client.id as string
                          });
                        },
                        text: "Share tax information"
                      }}
                    >
                      <WSText.ParagraphSm weight="book" color="gray700">
                        You have not consented to share your tax information
                        with the client. Future payments to you may not be
                        completed until the information is shared.
                      </WSText.ParagraphSm>
                    </WSAlert>
                  ) : areRequirementsCompleted && !hasEligibleEngagements ? (
                    <WSAlert
                      theme="warning"
                      icon="alert"
                      title="Payments eligibility pending"
                    >
                      You need to have at least one active assignment that is
                      eligible for payments to receive payments from this
                      client.
                    </WSAlert>
                  ) : !areRequirementsCompleted ? (
                    <WSAlert
                      theme="warning"
                      icon="alert"
                      title="Assignment requirements incomplete"
                    >
                      You will not be able to receive payouts for an assignment
                      until all requirements for that assignment are complete.
                    </WSAlert>
                  ) : expiringRequirements.length > 0 ||
                    expiredRequirements.length > 0 ? (
                    <>
                      {expiredRequirements.length > 0 && (
                        <WSAlert
                          theme="warning"
                          icon="alert"
                          title="Requirements incomplete"
                          action={{
                            text: "Renew now",
                            onClick: () => {
                              history.push(
                                getPayerDetailsRequirementsUrl(payerId)
                              );
                            }
                          }}
                        >
                          You must complete all eligibility requirements for an
                          assignment before you can receive payments for work
                          under that assignment.
                        </WSAlert>
                      )}

                      {expiringRequirements.length > 0 && (
                        <WSAlert
                          theme="neutral"
                          icon="calendar"
                          title="Requirements expiring soon"
                          action={{
                            text: "View requirements",
                            onClick: () => {
                              history.push(
                                getPayerDetailsRequirementsUrl(payerId)
                              );
                            }
                          }}
                        >
                          You have {expiringRequirements.length} requirements
                          expiring soon.
                        </WSAlert>
                      )}
                    </>
                  ) : null
                }

                <WSGrid gutter="2XL">
                  <WSGrid.Item span={{ m: "8" }}>
                    <Tabs
                      mb="L"
                      tabs={[
                        {
                          label: "Summary",
                          path: getPayerDetailsUrl(payerId)
                        },
                        {
                          label: "Requirements",
                          path: getPayerDetailsRequirementsUrl(payerId),
                          pill: {
                            icon: true,
                            theme:
                              !isNotShared && areRequirementsCompleted
                                ? "success"
                                : "warning"
                          }
                        }
                      ]}
                    />
                    <Switch>
                      <Route
                        path={PAYER_DETAILS_PATH}
                        exact
                        render={() => (
                          <WSList gap="L">
                            <WSElement>
                              <WSSectionToolbar title="Client information" />
                              <ClientInfoPanel client={client} />
                            </WSElement>
                            <WSElement>
                              <WSSectionToolbar title="Assignments" mb="M" />
                              {activeFilteredEngagements.length ? (
                                <EngagementsTable
                                  engagements={activeFilteredEngagements}
                                  requirements={allRequirements}
                                />
                              ) : (
                                <WSPanel>
                                  <WSPoster
                                    size="S"
                                    type="tasks"
                                    title="No assignments active"
                                    description="Once an assignment is added and active, it will appear here."
                                  />
                                </WSPanel>
                              )}
                            </WSElement>
                            {inactiveFilteredEngagements.length ? (
                              <WSElement>
                                <WSSectionToolbar
                                  title={
                                    showArchived ? "Archived assignments" : ""
                                  }
                                  mb="M"
                                  buttons={[
                                    {
                                      label: showArchived
                                        ? `Hide archived (${inactiveFilteredEngagements.length})`
                                        : `Show archived (${inactiveFilteredEngagements.length})`,
                                      onClick: () => {
                                        setShowArchived(v => !v);
                                      }
                                    }
                                  ]}
                                />
                                {showArchived ? (
                                  <EngagementsTable
                                    engagements={inactiveFilteredEngagements}
                                    requirements={allRequirements}
                                  />
                                ) : null}
                              </WSElement>
                            ) : null}
                          </WSList>
                        )}
                      />
                      <Route
                        path={PAYER_DETAILS_REQUIREMENTS_PATH}
                        render={() => (
                          <PayerPayeeDetailsRequirements
                            hasEligibleEngagements={hasEligibleEngagements}
                            requirements={requirements}
                            noActions={false}
                            archivedRequirements={archivedRequirements}
                            contextType={RequirementContextType.Payer}
                            isInactive={isInactive}
                            isLoading={isRequirementsLoading}
                            goToRequirement={(
                              requirement: PayeeRequirement
                            ) => {
                              history.push(
                                getPayerRequirementDetailUrl(
                                  payerId,
                                  requirement.id as string
                                )
                              );
                            }}
                          />
                        )}
                      />
                    </Switch>
                  </WSGrid.Item>

                  <WSGrid.Item span={{ m: "4" }}>
                    <WSList>
                      <RecentInvoicesWidget payerId={client.id as string} />
                      <WireTransferSection payerId={payerId} />

                      {/*
                        Documents — out until a payee can read a file the client
                        uploaded (see the banner at the top of this file):

                        {qDocuments?.data && qDocuments.data.length > 0 ? (
                          <DocumentsSection
                            isInactive={isInactive}
                            type={DocumentsSectionType.Client}
                            payerId={payerId}
                            payeeId={userId}
                          />
                        ) : null}
                      */}

                      <PayerIntegrationWidget
                        isInactive={isInactive}
                        payerId={client.id as string}
                        customerId={
                          queryQuickbooksMapping.data?.quickbooksCustomerId
                        }
                        itemId={queryQuickbooksMapping.data?.quickbooksItemId}
                      />

                      <AutoPay payerId={payerId} />

                      <ResourceHistory
                        resources={historyResources}
                        onViewAll={() => {
                          history.push(getPayerHistoryUrl(payerId));
                        }}
                      />
                    </WSList>
                  </WSGrid.Item>
                </WSGrid>
              </WSList>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
